import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ICustomerListModel, ITextChatConversationSummaryModel, ITextChatConversationModel, TextChatConversationModel, ISelectedLocationModel, SelectedLocationModel, TextChatMessageModel } from "@models";
import { TextChatStore } from "@stores";
import { Subscription } from "rxjs";
import { CustomersService, FunctionLockService, GlobalsService, HttpService, LookupService, SleepService, UtilsService } from "@services";
import { SlickDialogComponent, SlickScreenBlockerService } from "@slick-components";
import Swal from "sweetalert2";
import moment from "moment";

@Component({
	selector: "text-chat-conversations",
	templateUrl: "./text-chat-conversations.component.html",
	styleUrls: ['./text-chat-conversations.component.css'],
	providers: [CustomersService, FunctionLockService]
})

export class TextChatConversationsComponent implements OnInit, OnDestroy, OnChanges {
	@Output() onConversationSelected: EventEmitter<ITextChatConversationSummaryModel> = new EventEmitter<ITextChatConversationSummaryModel>();
	@ViewChild("startConversationDialogRef") startConversationDialogRef: SlickDialogComponent;

	tabIndex: number = 0;
	selectedLocations: ISelectedLocationModel[] = [];
	@Input() customerId: number;
	@Input() customerPhoneNumber: string;

	visibleConversations: ITextChatConversationSummaryModel[];
	activeConversation: ITextChatConversationModel;
	searchTimeout: NodeJS.Timer;

	newConversationSearchText: string;
	results: ICustomerListModel[];
	selectedCustomer: ICustomerListModel;
	newChatUuid: string;

	visibleConversations$: Subscription;
	activeConversation$: Subscription;

	customer: ICustomerListModel;
	companyTextNumber: string;


	constructor(public textChatStore: TextChatStore,
		private customersService: CustomersService,
		private httpService: HttpService,
		private functionLockService: FunctionLockService,
		private slickScreenBlockerService: SlickScreenBlockerService) {
		this.companyTextNumber = GlobalsService.company.textChatPhoneNumberDisplay;
	}

	async ngOnInit() {
		this.visibleConversations$ = this.textChatStore.visibleConversations$.subscribe(visibleConversations => this.visibleConversations = visibleConversations);
		this.activeConversation$ = this.textChatStore.activeConversation$.subscribe(conv => this.activeConversation = conv);

		// Wait for the init to complete
		await this.textChatStore.waitForInit()

		this.selectedLocations = this.textChatStore.selectedLocations;
		this.visibleConversations = this.textChatStore.getVisibleConversations();

		if (this.visibleConversations.length > 0) {
			const activeConversationUuid = this.textChatStore.getActiveConversationUuid();
			if (activeConversationUuid) {
				const activeConversation = this.visibleConversations.find(x => x.uuid === activeConversationUuid);
				if (activeConversation)
					this.selectConversation(activeConversation);
			}
			else
				this.selectConversation(this.visibleConversations[0]);
		}
	}

	ngOnDestroy() {
		if (this.visibleConversations$)
			this.visibleConversations$.unsubscribe();
		if (this.activeConversation$)
			this.activeConversation$.unsubscribe();
	}

	ngOnChanges() {
		if (this.customerId) {
			this.textChatStore.searchText = '';
			this.textChatStore.lastSearchText = '';
			this.reload();
		}
		else if (this.customerPhoneNumber) {
			this.textChatStore.searchText = this.customerPhoneNumber;
			this.textChatStore.lastSearchText = '';
			this.reload();
		}
	}

	async onKeyUp() {
		clearTimeout(this.searchTimeout);

		this.searchTimeout = setTimeout(async () => {

			if (this.textChatStore.searchText === this.textChatStore.lastSearchText)
				return;

			this.textChatStore.lastSearchText = this.textChatStore.searchText;

			this.reload();
		}, 700);
	}

	async onTabChanged() {
		if (this.textChatStore.displayType === this.tabIndex)
			return;

		await SleepService.sleep();

		this.textChatStore.displayType = this.tabIndex;
		this.reload();
	}

	showStartNewConversationDialog() {
		this.newConversationSearchText = null;
		this.results = null;
		this.customer = null;

		this.startConversationDialogRef.showDialog();
	}

	async findCustomer() {
		this.results = await this.customersService.getCustomersForList(this.newConversationSearchText, false, false, false);

		this.results = this.results.filter(x => !!x.cellPhone === true);

	}

	async onSelect(customer: ICustomerListModel) {
		this.newConversationSearchText = customer.cellPhone;
		this.customer = customer;
		

		this.startNewConversation();
	}

	onFreeformTextSelect(freeFormText: string) {
		this.newConversationSearchText = freeFormText;
	}

	async startNewConversation() {
		try {

			const customerIsBlackListed = await this.httpService.get(`textchat/CheckIfNumberIsBlackListed?number=${this.newConversationSearchText}`);
			if (customerIsBlackListed) {
				await Swal.fire({
					icon: 'warning',
					title: 'Oops...',
					text: "This number has opted out of text messaging",
					confirmButtonColor: '#007bff',
					heightAuto: false
				});
				return;
			}

			this.slickScreenBlockerService.block()
			const conversation = new TextChatConversationModel();
			conversation.uuid = UtilsService.newGuid();
			conversation.customerPhoneNumber = this.newConversationSearchText;
			if (this.customer) {
				conversation.customerId = this.customer.customerId;
				conversation.customerFullName = this.customer.customerFullName;
			}

			const newConversationSummary = await this.textChatStore.startConversation(conversation);
			this.textChatStore.loadActiveConversation(newConversationSummary.textChatConversationId);
			this.startConversationDialogRef.hideDialog();
		}
		finally {
			this.slickScreenBlockerService.unblock();
		}

	}

	async reload() {
		await this.functionLockService.lock("TEXT_CHAT_CONVERSATION_RELOAD")

		try {
			await SleepService.sleep();
			this.slickScreenBlockerService.forceBlock();
			this.visibleConversations = await this.textChatStore.refreshVisibleConversations(this.customerId);
			this.slickScreenBlockerService.forceUnblock();

			if (this.visibleConversations && this.visibleConversations.length > 0)
				await this.selectConversation(this.visibleConversations[0]);
			else
				this.textChatStore.clearActiveConversation();
		}
		finally {
			await this.functionLockService.release("TEXT_CHAT_CONVERSATION_RELOAD")
		}
	}

	async closeConversation(e: MouseEvent, conversation: ITextChatConversationSummaryModel) {
		e.stopPropagation();
		e.preventDefault();

		this.textChatStore.onCloseConversation(conversation);

		try {
			const closedMessage = new TextChatMessageModel();
			closedMessage.uuid = UtilsService.newGuid();
			closedMessage.textChatConversationId = conversation.textChatConversationId;
			closedMessage.sentDate = moment().utc().toDate();
			closedMessage.isSystemMessage = true;
			closedMessage.body = `Chat closed by ${GlobalsService.userInfo.fullName}`;
			this.textChatStore.addSystemMessage(closedMessage);

			await this.textChatStore.closeConversation(conversation.textChatConversationId);

			await SleepService.sleep(500);

			if(conversation.uuid === this.textChatStore.getActiveConversationUuid())
				this.textChatStore.clearActiveConversation();
		}
		catch {
		}
	}


	async selectConversation(conversation: ITextChatConversationSummaryModel) {
		await this.textChatStore.loadActiveConversation(conversation.textChatConversationId);
	}

	async onLocationChanged(selectedLocation: ISelectedLocationModel) {
		this.selectedLocations = await this.textChatStore.toggleSelectedLocation(
			selectedLocation.locationId,
			this.customerId
		);

		if (this.visibleConversations && this.visibleConversations.length > 0)
			await this.selectConversation(this.visibleConversations[0]);
		else
			this.textChatStore.clearActiveConversation();
	}
}
